import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import rxLogo from "../../assets/rx-logo.png";
import { useCurrency } from "../../context/CurrencyContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Menu, Globe2, LogOut, LayoutDashboard, Car, ShieldCheck, UserRound } from "lucide-react";
import NotificationBell from "../NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const { currencies, selectedCurrency, changeCurrency, settings } = useCurrency();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(`rx_mode_${user?.id}`) || "renting";
    } catch {
      return "hosting";
    }
  });
  useEffect(() => {
    if (!user?.id) return;
    try {
      const key = `rx_mode_${user.id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        setMode(saved);
      } else if (user.role === "owner") {
        setMode("hosting");
        localStorage.setItem(key, "hosting");
      }
    } catch {}
  }, [user?.id, user?.role]);
  const roleLabel = user?.role === "admin" ? "Admin" : mode === "hosting" ? "Owner" : "Renter";
  const switchMode = (next) => {
    setMode(next);
    try { localStorage.setItem(`rx_mode_${user.id}`, next); } catch {}
    navigate(next === "hosting" ? "/owner" : "/account");
  };

  const dashboardPath = user
    ? user.role === "admin"
      ? "/admin"
      : user.role === "owner"
      ? "/owner"
      : "/account"
    : null;

  const navLinks = (
    <>
      <NavLink
        to="/cars"
        data-testid="nav-cars"
        className={({ isActive }) =>
          `text-sm tracking-wide hover:text-primary transition-colors ${isActive ? "text-primary" : "text-foreground/80"}`
        }
      >
        Browse cars
      </NavLink>
      <NavLink
        to="/list-your-car"
        data-testid="nav-list-car"
        className={({ isActive }) =>
          `text-sm tracking-wide hover:text-primary transition-colors ${isActive ? "text-primary" : "text-foreground/80"}`
        }
      >
        List your car
      </NavLink>
      <NavLink
        to="/how-it-works"
        data-testid="nav-how"
        className={({ isActive }) =>
          `text-sm tracking-wide hover:text-primary transition-colors ${isActive ? "text-primary" : "text-foreground/80"}`
        }
      >
        How it works
      </NavLink>
    </>
  );

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-border" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to={user?.role === "owner" && mode === "hosting" ? "/owner" : "/"} className="flex items-center gap-2.5" data-testid="site-logo">
          <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
            <img src={rxLogo} alt="RydeXpress" className="w-full h-full object-cover" />
          </div>
          <div className="leading-none">
            <div className="font-display text-xl font-bold tracking-tight">RydeXpress</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground -mt-0.5 hidden sm:block">{settings?.platform_name?.includes("Rwanda") ? "Rwanda" : "Marketplace"}</div>
            <div className="text-[9px] leading-none font-bold uppercase text-[#2A9D8F] md:hidden">Home</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">{navLinks}</nav>

        <div className="hidden md:flex items-center gap-3">
          {user?.role === "owner" && (
            <div className="hidden lg:flex items-center rounded-full border border-border bg-muted/40 p-1 text-xs" data-testid="interface-switcher">
              <button
                type="button"
                onClick={() => switchMode("renting")}
                className={`px-3 py-1.5 rounded-full transition-colors ${mode === "renting" ? "bg-white shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="switch-to-renting"
              >
                Renting
              </button>
              <button
                type="button"
                onClick={() => switchMode("hosting")}
                className={`px-3 py-1.5 rounded-full transition-colors ${mode === "hosting" ? "bg-white shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="switch-to-hosting"
              >
                Hosting
              </button>
            </div>
          )}
          {user?.role === "customer" && (
            <Button
              variant="outline"
              className="hidden lg:flex text-xs h-9 border-[#2A9D8F] text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
              onClick={() => navigate("/list-your-car")}
              data-testid="become-host-header-btn"
            >
              Become a Host
            </Button>
          )}
          <Select value={selectedCurrency} onValueChange={changeCurrency}>
            <SelectTrigger data-testid="currency-selector" className="h-9 w-[88px] text-xs">
              <Globe2 size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                  {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!user ? (
            <>
              <Button
                variant="ghost"
                className="text-sm"
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
              >
                Sign in
              </Button>
              <Button
                className="btn-lift bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate("/register")}
                data-testid="header-register-btn"
              >
                Get started
              </Button>
            </>
          ) : (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-1 hover:bg-muted transition-colors"
                >
                  <Avatar className="h-7 w-7">
                    {user.profile_image_url && <AvatarImage src={user.profile_image_url} alt={user.name} />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm pr-1">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-display font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{roleLabel}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                  {user.role === "admin" ? <ShieldCheck size={14} className="mr-2" /> : <LayoutDashboard size={14} className="mr-2" />}
                  {user.role === "admin" ? "Admin console" : user.role === "owner" ? "Owner dashboard" : "My dashboard"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/account/profile")} data-testid="menu-profile">
                  <UserRound size={14} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationBell />}
          {user && (
            <Sheet open={mobileAccountOpen} onOpenChange={setMobileAccountOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-account-trigger" className="flex flex-col items-center justify-center gap-0.5">
                  <Avatar className="h-8 w-8">
                    {user.profile_image_url && <AvatarImage src={user.profile_image_url} alt={user.name} />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-[9px] leading-none text-muted-foreground">Profile</span>
                </button>
              </SheetTrigger>
              <SheetContent side="right">
                <div className="pb-4 border-b border-border">
                  <div className="font-display font-bold">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{roleLabel}</div>
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  <Button variant="outline" onClick={() => { setMobileAccountOpen(false); navigate(dashboardPath); }}>
                    Dashboard
                  </Button>
                  <Button variant="outline" onClick={() => { setMobileAccountOpen(false); navigate("/account/profile"); }}>
                    Profile
                  </Button>
                  {user.role === "owner" && (
                    <Button variant="outline" onClick={() => { setMobileAccountOpen(false); switchMode(mode === "hosting" ? "renting" : "hosting"); }}>
                      {mode === "hosting" ? "Switch to renting →" : "Switch to hosting →"}
                    </Button>
                  )}
                  {user.role === "customer" && (
                    <Button variant="outline" onClick={() => { setMobileAccountOpen(false); navigate("/list-your-car"); }}>
                      Become a Host →
                    </Button>
                  )}
                  <Button variant="destructive" onClick={async () => { await logout(); setMobileAccountOpen(false); navigate("/"); }}>
                    Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <span className="text-sm text-muted-foreground">Currency</span>
                <Select value={selectedCurrency} onValueChange={changeCurrency}>
                  <SelectTrigger className="h-8 w-[90px] text-xs" data-testid="mobile-currency-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <nav className="flex flex-col gap-2 mt-6">
                {[
                  { to: "/cars", label: "Browse cars" },
                  { to: "/list-your-car", label: "List your car" },
                  { to: "/how-it-works", label: "How it works" },
                ].map(({ to, label }) => (
                  <button key={to} onClick={() => { setMobileOpen(false); navigate(to); }}
                    className="text-left text-base py-2.5 px-3 rounded-lg hover:bg-muted transition-colors font-medium">
                    {label}
                  </button>
                ))}
              </nav>
              <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
                {user ? (
                  <>
                    <Button variant="outline" onClick={() => { setMobileOpen(false); navigate(dashboardPath); }}>
                      Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => { setMobileOpen(false); navigate("/account/profile"); }}>
                      Profile
                    </Button>
                    {user.role === "owner" && (
                      <Button variant="outline" onClick={() => { setMobileOpen(false); switchMode(mode === "hosting" ? "renting" : "hosting"); }}>
                        {mode === "hosting" ? "Switch to renting →" : "Switch to hosting →"}
                      </Button>
                    )}
                    {user.role === "customer" && (
                      <Button variant="outline" onClick={() => { setMobileOpen(false); navigate("/list-your-car"); }}>
                        Become a Host →
                      </Button>
                    )}
                    <Button variant="destructive" onClick={async () => { await logout(); setMobileOpen(false); navigate("/"); }}>
                      Sign out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => { setMobileOpen(false); navigate("/login"); }}>
                      Sign in
                    </Button>
                    <Button onClick={() => { setMobileOpen(false); navigate("/register"); }}>
                      Get started
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
